import $ from 'jquery';

import Autocomplete from 'chairisher/component/autocomplete';

import { getAccountLookupUrl } from 'chairisher/context/listingflow';

/**
 * A component that allows users to add commission field rows for a product.
 *
 * @param {Object} settings
 * @param {jQuery} settings.$containerEl
 * @param {jQuery} settings.$rowEl
 * @param {jQuery} settings.$addRowEl
 * @param {number=} settings.maxNumRows
 *
 * @constructor
 * @class AccountIDCommissionFields
 */
const AccountIDCommissionFields = function AccountIDCommissionFields(settings) {
    const options = $.extend(
        {
            $containerEl: this.$containerEl,
            $rowEl: this.$rowEl,
            $addRowEl: this.$addRowEl,
            maxNumRows: this.maxNumRows,
        },
        settings,
    );
    this.$containerEl = options.$containerEl;
    this.$rowEl = options.$rowEl;
    this.$addRowEl = options.$addRowEl;
    this.commissionDetailsMap.commission_details =
        'commissionDetailsMap' in options ? options.commissionDetailsMap : [];
    this.maxNumRows = options.maxNumRows;
    this.renderFieldsFromData();
    this.bind();
};

/**
 * Represents the container housing all the account id commission rows
 *
 * @type {jQuery}
 */
AccountIDCommissionFields.prototype.$containerEl = null;

/**
 * Represents a generic row of 3 account id commission fields
 *
 * @type {jQuery}
 */
AccountIDCommissionFields.prototype.$rowEl = null;

/**
 * Add a new row of account id commission fields
 *
 * @type {jQuery}
 */
AccountIDCommissionFields.prototype.$addRowEl = null;

/**
 * Prefix for the ID of each row
 *
 * @type {string}
 */
AccountIDCommissionFields.prototype.genericRowID = 'account-id-commission-row-';

/**
 * The maximum commission field rows we should have on a page
 *
 * @type {number}
 */
AccountIDCommissionFields.prototype.maxNumRows = 10;

/**
 * The map of account id commission field values that is sent to the backend
 *
 * @type {Object}
 */
AccountIDCommissionFields.prototype.commissionDetailsMap = {};

/**
 * Returns the set of all account id commission rows
 *
 * @returns {jQuery}
 */
AccountIDCommissionFields.prototype.getRowsEl = function getRowsEl() {
    return this.$containerEl.children();
};

/**
 * Returns a generic row element containing the account id commission fields
 *
 * @returns {jQuery}
 */
AccountIDCommissionFields.prototype.getRowEl = function getRowEl() {
    return this.$rowEl;
};

/**
 * Returns the row index from the row id
 *
 * @returns {number}
 */
AccountIDCommissionFields.prototype.getRowIdx = function getRowIdx(rowDiv) {
    return rowDiv.split(this.genericRowID)[1];
};

/**
 * Returns the add new row element
 *
 * @returns {jQuery}
 */
AccountIDCommissionFields.prototype.getAddRowEl = function getAddRowEl() {
    return $('.js-add-account-id-commission-btn');
};

/**
 * Returns the map of account id commission field values that is sent to the backend
 *
 * @returns {Object}
 */
AccountIDCommissionFields.prototype.getValue = function getValue() {
    const tempDetailsList = [];
    const tempDetailsMap = {};

    for (let i = 0; i < this.commissionDetailsMap.commission_details.length; i += 1) {
        const tempCommissionDetailMap = {};
        tempCommissionDetailMap.account_id = this.commissionDetailsMap.commission_details[i].account_id;
        tempCommissionDetailMap.is_pct = this.commissionDetailsMap.commission_details[i].is_pct;
        tempCommissionDetailMap.amount = this.commissionDetailsMap.commission_details[i].amount;
        tempDetailsList.push(tempCommissionDetailMap);
    }

    tempDetailsMap.commission_details = tempDetailsList;

    return tempDetailsMap;
};

/**
 * Add a new row of commission fields
 */
AccountIDCommissionFields.prototype.addRow = function addRow() {
    const numRows = this.getRowsEl().length;

    if (numRows < this.maxNumRows) {
        if (numRows === this.maxNumRows - 1) {
            this.getAddRowEl().addClass('hidden');
        }
        let $placeholderEl = this.getRowEl().clone();

        $placeholderEl.find('[name=account_id]').val('');
        $placeholderEl.find('[name=is_account_id_commission_pct]').val(0);
        $placeholderEl.find('[name=account_id_commission_value]').val('');

        const rowNameAndId = this.genericRowID + numRows;

        $placeholderEl.attr({
            name: rowNameAndId,
            id: rowNameAndId,
        });

        // we default showing new rows as percentages
        $placeholderEl = this.renderPlaceholderWidget($placeholderEl, true);

        this.$containerEl.append($placeholderEl);
        this.renderAutocomplete();
    }
};

/**
 * Remove a row of commission fields
 *
 * @param {jquery} $el
 */
AccountIDCommissionFields.prototype.removeRow = function removeRow($el) {
    // delete a specific row of commission fields
    const closestRow = $el.closest(this.getRowsEl());

    const rowIdx = this.getRowIdx(closestRow[0].id);
    this.commissionDetailsMap.commission_details.splice(rowIdx, 1);

    closestRow.remove();

    if (this.getAddRowEl().is(':hidden')) {
        this.getAddRowEl().removeClass('hidden');
    }
};

/**
 * Update the commission details map with current field values
 */
AccountIDCommissionFields.prototype.buildCommissionMap = function buildCommissionMap(rowIdx, fieldName, fieldValue) {
    // On any change in card, we want to update our commission details map that we send over to the backend
    if (rowIdx < this.commissionDetailsMap.commission_details.length) {
        this.commissionDetailsMap.commission_details[rowIdx][fieldName] = fieldValue;
    } else {
        const tempCommissionDetailsMap = {};
        tempCommissionDetailsMap[fieldName] = fieldValue;
        this.commissionDetailsMap.commission_details.push(tempCommissionDetailsMap);
    }
};

/**
 * Binds actions to the various elements that comprise the AccountIDCommissionFields
 */
AccountIDCommissionFields.prototype.bind = function bind() {
    $('.js-account-id-commission-rows')
        .on('autocomplete:selected', '.js-account-id-commission-row', (e, item) => {
            const rowIdx = this.getRowIdx(e.currentTarget.id);

            this.buildCommissionMap(rowIdx, 'account_id', parseInt(item.id, 10), item.email);
            this.buildCommissionMap(rowIdx, 'email', item.email);
            this.renderFieldsFromData();
        })
        .on('change', '.js-commission-is-pct', (e) => {
            const rowIdx = this.getRowIdx($(e.target).closest(this.getRowsEl())[0].id);
            this.buildCommissionMap(rowIdx, 'is_pct', e.target.value === '0');
            this.renderFieldsFromData();
        })
        .on('change', '.js-commission-value', (e) => {
            const rowIdx = this.getRowIdx($(e.target).closest(this.getRowsEl())[0].id);
            let commissionValue = e.target.value;
            if (commissionValue) {
                commissionValue = parseFloat(commissionValue);
            }
            this.buildCommissionMap(rowIdx, 'amount', commissionValue);
            this.renderFieldsFromData();
        })
        .on('click', '.js-remove-account-id-commission-row-btn', (e) => {
            this.removeRow($(e.target)); // delete row
            this.renderFieldsFromData();
        })
        .on('mouseenter', '.js-account-id-commission-row', (e) => {
            $(e.target)
                .closest(this.getRowsEl())
                .find('.js-remove-account-id-commission-row-btn')
                .removeClass('hidden'); // show remove btn
        })
        .on('mouseleave', '.js-account-id-commission-row', (e) => {
            $(e.target).closest(this.getRowsEl()).find('.js-remove-account-id-commission-row-btn').addClass('hidden'); // hide remove btn
        });

    // add a row when we click a button
    this.getAddRowEl().on('click', () => {
        this.addRow(); // we render the row here so don't need to render again
        const rowIdx = this.getRowsEl().length;
        this.buildCommissionMap(rowIdx, 'is_pct', true); // default to is_pct being true on row creation
    });
};

/**
 * Use the form data to render the $ or % widget depending on if we have a percent or fixed value.
 *
 * @param {jQuery} $placeholderEl
 * @param {boolean} isAccountIdCommissionPct
 * @returns {jQuery}
 */
AccountIDCommissionFields.prototype.renderPlaceholderWidget = function renderPlaceholderWidget(
    $placeholderEl,
    isAccountIdCommissionPct,
) {
    const $placeholderCommissionValueWidget = $placeholderEl
        .find('[name=account_id_commission_value]')
        .siblings('.input-group-addon');

    if (isAccountIdCommissionPct) {
        $placeholderCommissionValueWidget
            .text('%')
            .insertAfter($placeholderEl.find('[name=account_id_commission_value]'));
    } else {
        $placeholderCommissionValueWidget.text('$');
    }

    return $placeholderEl;
};

/**
 * Attach an autocomplete field to each account id
 */
AccountIDCommissionFields.prototype.renderAutocomplete = function renderAutocomplete() {
    this.getRowsEl().each((_, elem) => {
        const $queryEl = $(elem).find('[name=account_email]');
        $queryEl.attr('placeholder', 'E-mail/Account ID');

        // eslint-disable-next-line no-new
        new Autocomplete({
            $queryEl,
            dataDisplayKey: 'email',
            dataObject: {
                limit: 20,
            },
            dataEndpoint: getAccountLookupUrl(),
            isMultiSelect: false,
        });
    });
};

/**
 * Use the form data to render the account ID commission row elements
 */
AccountIDCommissionFields.prototype.renderFieldsFromData = function renderFieldsFromData() {
    const data = this.commissionDetailsMap.commission_details;
    this.$containerEl.empty();

    const $placeholderEl = $('<div></div>');
    for (let i = 0; i < data.length; i += 1) {
        $placeholderEl.append(this.marshalFields(data[i], i));
    }

    this.$containerEl.append($placeholderEl.children()); // append children all at once to avoid reflows
    this.renderAutocomplete();
};

/**
 * Create each row of account ID commission data fill with data
 *
 * @param {Object} data
 * @param {number} counter
 * @returns {jQuery|undefined}
 */
AccountIDCommissionFields.prototype.marshalFields = function marshalFields(data, counter) {
    if (data) {
        let $placeholderRowEl = this.getRowEl().clone();

        $placeholderRowEl = this.renderPlaceholderWidget($placeholderRowEl, data.is_pct);

        const accountEmail = data.email;
        const isAccountIdCommissionPct = data.is_pct ? 0 : 1;
        const accountIdCommissionValue = data.amount;

        $placeholderRowEl.find('[name=account_email]').val(accountEmail);
        $placeholderRowEl.find('[name=is_account_id_commission_pct]').val(isAccountIdCommissionPct);
        $placeholderRowEl.find('[name=account_id_commission_value]').val(accountIdCommissionValue);

        const rowNameAndId = this.genericRowID + counter;

        $placeholderRowEl.attr({
            name: rowNameAndId,
            id: rowNameAndId,
        });

        return $placeholderRowEl;
    }

    return undefined;
};

export default AccountIDCommissionFields;
